import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faTimes, faCheck, faEdit, faTrash,
    faPalette, faEye, faEyeSlash, faSync
} from '@fortawesome/free-solid-svg-icons';
import baseURL from '../../url';
import './ColorManager.css';

// Componente Toast
const Toast = ({ toasts }) => {
    if (toasts.length === 0) return null;
    return (
        <div className="color-toast-container">
            {toasts.map(t => (
                <div key={t.id} className={`color-toast ${t.type}`}>
                    <div className="toast-icon">
                        {t.type === 'success' && '✅'}
                        {t.type === 'error' && '❌'}
                        {t.type === 'warning' && '⚠️'}
                        {t.type === 'info' && 'ℹ️'}
                    </div>
                    <div className="toast-content">
                        <div className="toast-title">{t.message}</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default function ColorManager({ onColorsUpdated }) {
    const [colores, setColores] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingColor, setEditingColor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [toasts, setToasts] = useState([]);

    const [formData, setFormData] = useState({
        nombre: '',
        valor: '#000000'
    });

    // Helper para mostrar toasts
    const showToast = (message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    };

    // Cargar colores
    const cargarColores = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${baseURL}/coloresGet.php`);
            const data = await response.json();

            if (data.success) {
                setColores(data.colores);
                onColorsUpdated?.(data.colores);
            } else {
                showToast(data.error || 'Error al cargar colores', 'error');
            }
        } catch (error) {
            showToast('Error de conexión al cargar colores', 'error');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarColores();
    }, []);

    // Abrir modal para agregar
    const abrirModalAgregar = () => {
        setEditingColor(null);
        setFormData({ nombre: '', valor: '#000000' });
        setModalOpen(true);
    };

    // Abrir modal para editar
    const abrirModalEditar = (color) => {
        setEditingColor(color);
        setFormData({
            nombre: color.nombre,
            valor: color.valor
        });
        setModalOpen(true);
    };

    // Cerrar modal
    const cerrarModal = () => {
        setModalOpen(false);
        setEditingColor(null);
        setFormData({ nombre: '', valor: '#000000' });
    };

    // Guardar color (agregar o editar)
    const guardarColor = async () => {
        if (!formData.nombre.trim()) {
            showToast('El nombre del color es obligatorio', 'warning');
            return;
        }

        if (!formData.valor.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)) {
            showToast('Ingrese un código hexadecimal válido', 'warning');
            return;
        }

        setLoading(true);
        try {
            const url = editingColor
                ? `${baseURL}/coloresPut.php`
                : `${baseURL}/coloresPost.php`;

            const payload = editingColor
                ? { ...formData, idColor: editingColor.idColor }
                : formData;

            const response = await fetch(url, {
                method: editingColor ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success) {
                showToast(data.mensaje, 'success');
                await cargarColores();
                cerrarModal();
            } else {
                showToast(data.error, 'error');
            }
        } catch (error) {
            showToast('Error de conexión', 'error');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Alternar estado activo/inactivo
    const toggleActivo = async (color) => {
        setLoading(true);
        try {
            const response = await fetch(`${baseURL}/coloresPut.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idColor: color.idColor,
                    activo: color.activo ? 0 : 1
                })
            });

            const data = await response.json();

            if (data.success) {
                showToast(`Color ${color.activo ? 'desactivado' : 'activado'}`, 'success');
                await cargarColores();
            } else {
                showToast(data.error, 'error');
            }
        } catch (error) {
            showToast('Error de conexión', 'error');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Eliminar color
    const eliminarColor = async (color) => {
        if (!window.confirm(`¿Estás seguro de eliminar el color "${color.nombre}"?`)) {
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${baseURL}/coloresDelete.php?idColor=${color.idColor}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (data.success) {
                showToast(data.mensaje, 'success');
                await cargarColores();
            } else {
                showToast(data.error, 'error');
            }
        } catch (error) {
            showToast('Error de conexión', 'error');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="color-manager">
            <Toast toasts={toasts} />

            <div className="color-manager-header">
                <div className="header-title">
                    <FontAwesomeIcon icon={faPalette} />
                    <span>Gestión de Colores</span>
                    <div className="color-count">{colores.length} colores</div>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-refresh"
                        onClick={cargarColores}
                        disabled={loading}
                        title="Recargar colores"
                    >
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                    </button>
                    <button
                        className="btn-add-color"
                        onClick={abrirModalAgregar}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Agregar Color
                    </button>
                </div>
            </div>

            <div className="colors-grid">
                {colores.map(color => (
                    <div
                        key={color.idColor}
                        className={`color-card ${!color.activo ? 'inactive' : ''}`}
                    >
                        <div className="color-preview-section">
                            <div
                                className="color-circle"
                                style={{ backgroundColor: color.valor }}
                                title={`${color.nombre} - ${color.valor}`}
                            />
                            <div className="color-info">
                                <div className="color-name">{color.nombre}</div>
                                <div className="color-hex">{color.valor}</div>
                            </div>
                        </div>

                        <div className="color-actions">
                            <button
                                className="btn-toggle-active"
                                onClick={() => toggleActivo(color)}
                                title={color.activo ? 'Desactivar' : 'Activar'}
                            >
                                <FontAwesomeIcon icon={color.activo ? faEye : faEyeSlash} />
                            </button>
                            <button
                                className="btn-edit"
                                onClick={() => abrirModalEditar(color)}
                                title="Editar color"
                            >
                                <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                                className="btn-delete"
                                onClick={() => eliminarColor(color)}
                                title="Eliminar color"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {colores.length === 0 && !loading && (
                <div className="empty-state">
                    <FontAwesomeIcon icon={faPalette} size="3x" />
                    <h3>No hay colores configurados</h3>
                    <p>Agrega colores para usar en tus productos</p>
                    <button className="btn-add-color" onClick={abrirModalAgregar}>
                        <FontAwesomeIcon icon={faPlus} />
                        Agregar Primer Color
                    </button>
                </div>
            )}

            {/* Modal */}
            {modalOpen && (
                <div className="color-modal-backdrop">
                    <div className="color-modal">
                        <div className="modal-header">
                            <h3>
                                <FontAwesomeIcon icon={faPalette} />
                                {editingColor ? 'Editar Color' : 'Agregar Nuevo Color'}
                            </h3>
                            <button className="btn-close" onClick={cerrarModal}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label>Nombre del Color *</label>
                                <input
                                    type="text"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                    placeholder="Ej: Rojo Intenso"
                                    maxLength="50"
                                />
                            </div>

                            <div className="form-group">
                                <label>Código Hexadecimal *</label>
                                <div className="color-input-group">
                                    <input
                                        type="color"
                                        value={formData.valor}
                                        onChange={(e) => setFormData(prev => ({ ...prev, valor: e.target.value }))}
                                        className="color-picker"
                                    />
                                    <input
                                        type="text"
                                        value={formData.valor}
                                        onChange={(e) => setFormData(prev => ({ ...prev, valor: e.target.value }))}
                                        placeholder="#FF0000"
                                        pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                                        className="hex-input"
                                    />
                                </div>
                            </div>

                            <div className="color-preview">
                                <div className="preview-label">Vista Previa:</div>
                                <div
                                    className="preview-circle"
                                    style={{ backgroundColor: formData.valor }}
                                />
                                <div className="preview-info">
                                    <div>{formData.nombre || 'Sin nombre'}</div>
                                    <div>{formData.valor}</div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={cerrarModal}>
                                Cancelar
                            </button>
                            <button
                                className="btn-save"
                                onClick={guardarColor}
                                disabled={loading || !formData.nombre.trim()}
                            >
                                <FontAwesomeIcon icon={faCheck} />
                                {editingColor ? 'Actualizar' : 'Agregar'} Color
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}